import { AppDispatch, RootState } from '@/stores'
import { updateComponentAction, replaceTBoltAction } from '@/stores/active-design'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as v from 'valibot'
import PanelTitle from './components/PanelTitle'
import PanelSections from './components/PanelSections'
import PanelSection from './components/PanelSection'
import { useGetProductVariantsQuery, useGetProductsQuery } from '@/api/serverApi'
import { LoadingSpinner } from '@cfs-fixings/shared'
import PanelInput from './components/PanelInput'
import { useForm, Controller } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import PanelSelect from './components/PanelSelect'
import ComponentType from '@/enums/ComponentType'
import useComponentProduct from '@/hooks/useComponentProduct'
import ConfirmationModal from '@/components/modals/ConfirmationModal'
import useComponentParent from '@/hooks/useComponentParent'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'

export type TriggerUpdateKey = keyof NonNullable<Parameters<typeof updateComponentAction>[0]['details']['t_bolt']>

export const tboltSchema = v.object({
  t_bolt: v.optional(
    v.object({
      shear_force: v.optional(v.number([v.minValue(0)])),
      tension_force: v.optional(v.number([v.minValue(0)])),
      length: v.optional(v.number([v.integer()])),
      diameter: v.optional(v.string()),
      variant_name: v.optional(v.string()),
      stand_off_distance: v.optional(v.number([v.minValue(0)])),
    }),
  ),
  position: v.optional(v.object({
    x: v.optional(v.number([v.integer()])),
  })),
})

export type TBoltFormInputs = v.Input<typeof tboltSchema>

const TboltProperties = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [errorKeys, setErrorKeys] = useState<string[]>([])
  const [isConfirmTBoltOpen, setIsConfirmTBoltOpen] = useState(false)
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)

  const selectedComponentId = useSelector((state: RootState) => state.activeDesign.selectedComponentId)

  const component = useSelector((state: RootState) => state.activeDesign.design?.components?.find(c => c.id === selectedComponentId))

  const { parent: channel } = useComponentParent({ component })

  const { register, setValue, control } = useForm<TBoltFormInputs>({
    resolver: valibotResolver(tboltSchema),
  })

  const channelId = component?.parent_id

  useEffect(() => {
    setValue('t_bolt.length', component?.t_bolt?.length)
    setValue('t_bolt.diameter', component?.t_bolt?.diameter)
    setValue('position', component?.position)
    setValue('t_bolt.shear_force', parseFloat(component?.t_bolt?.shear_force ?? '0'))
    setValue('t_bolt.tension_force', parseFloat(component?.t_bolt?.tension_force ?? '0'))
    setValue('t_bolt.variant_name', component?.product_variant_id)
  }, [component, setValue])

  const { data: products, isFetching: isFetchingProducts } = useGetProductsQuery({
    perPage: 10000,
  })

  const { data: productVariants, isFetching: isFetchingProductVariants } = useGetProductVariantsQuery({
    productType: ComponentType.TBolt,
    perPage: 10000,
  })

  const productVariant = productVariants?.data.find(p => p.id === component?.product_variant_id)
  const product = products?.data.find(p => p.id === productVariant?.product_id)

  const parentComponent = useSelector((state: RootState) => state.activeDesign.design?.components?.find(c => c.id === channelId))

  const { productVariant: parentProductVariant } = useComponentProduct({ component: parentComponent })

  const compatibleChildren = parentProductVariant?.compatible_children ?? []

  const isFetching = isFetchingProducts || isFetchingProductVariants

  const triggerUpdate = async (key: TriggerUpdateKey, value: any) => {
    const vResult = v.safeParse(tboltSchema, { [key]: value })

    if (!vResult.success) {
      setErrorKeys([
        ...errorKeys,
        ...vResult.issues.map(i => (
          i.path!.reduce((acc, curr) => [...acc, curr.key as string], [] as string[]).join('.')
        )),
      ])
      return
    }

    setErrorKeys(errorKeys.filter(k => k !== key))

    dispatch(updateComponentAction({
      componentId: selectedComponentId!,
      details: {
        t_bolt: {
          [key]: value,
        },
      },
    }))
  }

  const updatePosition = (key: 'x' | 'y', value: string) => {
    const parsedValue = value.trim() !== '' ? parseInt(value) : 0

    const channelLength = channel?.cast_in_anchor_channel?.length ?? channel?.cast_in_anchor_channel?.custom_length

    if (key === 'x') {
      if (parsedValue < 0 || parsedValue > channelLength) {
        toastHelper.dismissibleError('Invalid T-Bolt positioning', `Position must be between 0 and ${channelLength}`)
        setValue('position.x', channelLength)
        return
      }
    }

    const vResult = v.safeParse(tboltSchema, { position: { [key]: parsedValue } })

    if (!vResult.success) {
      setErrorKeys([
        ...errorKeys,
        ...vResult.issues.map(i => (
          i.path!.reduce((acc, curr) => [...acc, curr.key as string], [] as string[]).join('.')
        )),
      ])
      return
    }

    setErrorKeys(errorKeys.filter(k => k !== `position.${key}`))

    dispatch(updateComponentAction({
      componentId: selectedComponentId!,
      details: {
        position: {
          [key]: parsedValue,
        },
      },
    }))
  }

  const handleConfirmReplace = async () => {
    if (selectedVariantId) {
      dispatch(replaceTBoltAction({
        componentId: selectedComponentId!,
        body: {
          new_product_variant_id: selectedVariantId,
        },
      }))
    }
    setIsConfirmTBoltOpen(false)
  }

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner className="w-4 h-4" />
      </div>
    )
  }

  return (
    <>
      <PanelTitle>Bolt</PanelTitle>

      <PanelSections>
        <PanelSection>
          <div className="flex gap-[10px] items-center">
            <img
              src={product?.image}
              alt=""
              className="w-[47px] h-[47px] object-cover rounded-[2px]"
            />

            <div>
              <h3 className="font-semibold text-[14px] text-gray-900">
                {productVariant?.name}
              </h3>
              {/* {
                product?.link && (
                  <a
                    className="font-medium text-[12px] underline text-green-900"
                    href={product?.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View Product Details
                  </a>
                )
              } */}
            </div>
          </div>
        </PanelSection>

        <PanelSection title="Variant Name">
          <Controller
            control={control}
            name={'t_bolt.variant_name'}
            render={({ field }) => (
              <div className="space-y-[10px]">
                <PanelSelect
                  {...register('t_bolt.variant_name')}
                  value={field.value}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    setSelectedVariantId(selectedId)
                    setIsConfirmTBoltOpen(true)
                  }}
                >
                  {compatibleChildren?.map(variant => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name}
                    </option>
                  ))}
                </PanelSelect>
              </div>
            )}
          />
        </PanelSection>

        <PanelSection title="Static Forces">
          <div className="space-y-[10px]">
            <PanelInput
              title="Shear (VEd)"
              {...register('t_bolt.shear_force')}
              onBlur={e => triggerUpdate('shear_force', parseFloat(e.target.value))}
              error={errorKeys.includes('shear_force')}
              units="kN"
            />
            <PanelInput
              title="Tension (NEd)"
              {...register('t_bolt.tension_force')}
              onBlur={e => triggerUpdate('tension_force', parseFloat(e.target.value))}
              error={errorKeys.includes('tension_force')}
              units="kN"
            />
          </div>
        </PanelSection>

        <PanelSection title="Position X">
          <div className="space-y-[10px]">
            <PanelInput
              title="Position X"
              {...register('position.x')}
              onBlur={e => updatePosition('x', e.target.value)}
              error={errorKeys.includes('position.x')}
              onChange={e => {
                const value = e.currentTarget.value
                if (value === '') {
                  return setValue('position.x', 0)
                }
                const parsedValue = parseInt(value)
                const channelLength = channel?.cast_in_anchor_channel?.length ?? channel?.cast_in_anchor_channel?.custom_length
                if (parsedValue < 0 || parsedValue > channelLength) {
                  toastHelper.dismissibleError('Invalid Position', `Position must be between 0 and ${channelLength} to fit the channel`)
                  setValue('position.x', channelLength)
                  return
                }
                setValue('position.x', parsedValue)
              }}
            />
          </div>
        </PanelSection>

        <PanelSection title="Length">
          <div className="space-y-[10px]">
            <PanelSelect
              {...register('t_bolt.length')}
              value={component?.t_bolt?.length}
              onChange={e => triggerUpdate('length', e.target.value)}
            >
              {
                productVariant?.t_bolt?.lengths.map(length => (
                  <option key={length} value={length}>{length}mm</option>
                ))
              }
            </PanelSelect>
          </div>
        </PanelSection>

        <PanelSection title="Diameter">
          <div className="space-y-[10px]">
            <PanelSelect
              {...register('t_bolt.diameter')}
              value={component?.t_bolt?.diameter}
              onChange={e => triggerUpdate('diameter', e.target.value)}
            >
              {
                productVariant?.t_bolt?.diameters.map(diameter => (
                  <option key={diameter.name} value={diameter.name}>{diameter.name}</option>
                ))
              }
            </PanelSelect>
          </div>
        </PanelSection>
      </PanelSections>

      <ConfirmationModal
        isOpen={isConfirmTBoltOpen}
        onClose={() => setIsConfirmTBoltOpen(false)}
        onConfirm={handleConfirmReplace}
        title="Confirm Replacement"
        message="Are you sure you want to replace the T-bolt with the selected variant?"
      />
    </>
  )
}

export default TboltProperties
